"use client"

import type React from "react"
import { useState, useEffect, useMemo, useCallback } from "react"
import DataTable, { type TableColumn } from "react-data-table-component"
import { Card, CardHeader, CardTitle, CardBody, Button, Input, Row, Col, FormGroup, Label } from "reactstrap"
import DatePicker from "react-datepicker"
import "react-datepicker/dist/react-datepicker.css"
import { format, subDays } from "date-fns"
import { toast } from "react-toastify"
import { FaCalendarAlt, FaDownload, FaSearch, FaTimes, FaCar } from "react-icons/fa"
import ExhibitorDashboardService from "../../../Service/exhibitorDashboardService"
import type { ExhibitorVehicleAttendance } from "./types"
import { formatDateForDisplay } from "./dashboardHelpers"

const VehicleAttendance: React.FC = () => {
  // Set default date range: last 7 days
  const defaultStartDate = subDays(new Date(), 7) // 7 days ago
  const defaultEndDate = new Date() // Today

  const [data, setData] = useState<ExhibitorVehicleAttendance[]>([])
  const [loading, setLoading] = useState(false)
  const [totalRows, setTotalRows] = useState(0)
  const [perPage, setPerPage] = useState(10)
  const [currentPage, setCurrentPage] = useState(1)
  const [startDate, setStartDate] = useState<Date>(defaultStartDate)
  const [endDate, setEndDate] = useState<Date>(defaultEndDate)
  const [filterText, setFilterText] = useState("")
  const [resetPaginationToggle, setResetPaginationToggle] = useState(false)

  const fetchReports = useCallback(async (page: number, size: number, start: Date, end: Date, search?: string) => {
    setLoading(true)
    try {
      const params: any = {
        page,
        page_size: size,
        start_date: format(start, "yyyy-MM-dd"),
        end_date: format(end, "yyyy-MM-dd"),
        search: search || undefined,
      }
      const response = await ExhibitorDashboardService.getExhibitorVehicleAttendance(params)
      setData(response.results)
      setTotalRows(response.count)
    } catch (error) {
      toast.error("Failed to fetch vehicle attendance.")
      console.error("Error fetching vehicle attendance:", error)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchReports(currentPage, perPage, startDate, endDate, filterText)
  }, [currentPage, perPage, startDate, endDate, filterText, fetchReports])

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
  }

  const handlePerRowsChange = (newPerPage: number, page: number) => {
    setPerPage(newPerPage)
    setCurrentPage(page)
  }

  // Separate handlers for start and end date
  const handleStartDateChange = (date: Date | null) => {
    if (date) {
      setStartDate(date)
      setCurrentPage(1) // Reset to first page on date change

      // Validate that start date is not after end date
      if (date > endDate) {
        toast.warning("Start date cannot be after end date")
        return
      }
    }
  }

  const handleEndDateChange = (date: Date | null) => {
    if (date) {
      setEndDate(date)
      setCurrentPage(1) // Reset to first page on date change

      // Validate that end date is not before start date
      if (date < startDate) {
        toast.warning("End date cannot be before start date")
        return
      }
    }
  }

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFilterText(e.target.value)
    setCurrentPage(1) // Reset to first page on search
    setResetPaginationToggle(!resetPaginationToggle)
  }

  const handleClearFilter = () => {
    setFilterText("")
    setStartDate(defaultStartDate) // Reset to default start date
    setEndDate(defaultEndDate) // Reset to default end date
    setCurrentPage(1)
    setResetPaginationToggle(!resetPaginationToggle)
    toast.info("Filters cleared")
  }

  const handleExport = async () => {
    try {
      const params: any = {
        start_date: format(startDate, "yyyy-MM-dd"),
        end_date: format(endDate, "yyyy-MM-dd"),
        search: filterText || undefined,
      }
      const blob = await ExhibitorDashboardService.exportExhibitorVehicleAttendance(params)
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement("a")
      link.href = url
      link.setAttribute("download", `exhibitor_vehicle_attendance_${format(new Date(), "yyyyMMdd_HHmmss")}.csv`)
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
      toast.success("Vehicle attendance exported successfully!")
    } catch (error) {
      toast.error("Failed to export vehicle attendance.")
      console.error("Error exporting vehicle attendance:", error)
    }
  }

  const filteredData = useMemo(() => {
    if (!filterText) {
      return data
    }
    return data.filter((item) => {
      return (
        item.plate?.toLowerCase().includes(filterText.toLowerCase()) ||
        item.status?.toLowerCase().includes(filterText.toLowerCase()) ||
        item.gate_assignment__gate?.toLowerCase().includes(filterText.toLowerCase())
      )
    })
  }, [data, filterText])

  const columns: TableColumn<ExhibitorVehicleAttendance>[] = useMemo(
    () => [
      {
        name: "ID",
        selector: (row) => row.id,
        sortable: true,
        width: "80px",
      },
      {
        name: "Plate Number",
        selector: (row) => row.plate,
        sortable: true,
        minWidth: "150px",
      },
      {
        name: "Status",
        selector: (row) => row.status,
        sortable: true,
        width: "120px",
        cell: (row) => (
          <span className={`badge ${row.status === "active" ? "bg-success" : "bg-secondary"}`}>{row.status}</span>
        ),
      },
      {
        name: "Time Entered",
        selector: (row) => row.entered_at || "N/A",
        sortable: true,
        minWidth: "180px",
        cell: (row) => (row.entered_at ? formatDateForDisplay(row.entered_at) : "N/A"),
      },
      {
        name: "Time Exited",
        selector: (row) => row.exited_at || "Still Inside",
        sortable: true,
        minWidth: "180px",
        cell: (row) =>
          row.exited_at ? formatDateForDisplay(row.exited_at) : <span className="text-info">Still Inside</span>,
      },
      {
        name: "Gate Number",
        selector: (row) => row.gate_assignment__gate,
        sortable: true,
        width: "120px",
      },
    ],
    [],
  )

  const customStyles = {
    headCells: {
      style: {
        fontWeight: "bold",
        backgroundColor: "#f8f9fa",
      },
    },
    rows: {
      highlightOnHoverStyle: {
        backgroundColor: "#f0f0f0",
      },
    },
  }

  // Calculate date range info
  const dateRangeInfo = useMemo(() => {
    const daysDiff = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24))
    return `${daysDiff + 1} day${daysDiff !== 0 ? "s" : ""}`
  }, [startDate, endDate])

  return (
    <Card className="shadow-sm">
      <CardHeader>
        <CardTitle className="d-flex align-items-center justify-content-between">
          <div>
            <span className="d-flex align-items-center">
              <FaCar className="me-2" />
              Vehicle Attendance
            </span>
            <small className="text-muted d-block">
              Showing data for {dateRangeInfo} ({format(startDate, "MMM dd, yyyy")} - {format(endDate, "MMM dd, yyyy")})
            </small>
          </div>
          <Button color="success" size="sm" onClick={handleExport} className="d-flex align-items-center">
            <FaDownload className="me-1" size={12} />
            Export CSV
          </Button>
        </CardTitle>
      </CardHeader>
      <CardBody>
        {/* Filters Section */}
        <Row className="mb-4">
          <Col md={3}>
            <FormGroup>
              <Label className="fw-bold">
                <FaCalendarAlt className="me-1" size={12} />
                Start Date
              </Label>
              <DatePicker
                selected={startDate}
                onChange={handleStartDateChange}
                dateFormat="yyyy-MM-dd"
                placeholderText="Select start date"
                className="form-control"
                maxDate={endDate} // Prevent selecting start date after end date
                showYearDropdown
                showMonthDropdown
                dropdownMode="select"
              />
            </FormGroup>
          </Col>
          <Col md={3}>
            <FormGroup>
              <Label className="fw-bold">
                <FaCalendarAlt className="me-1" size={12} />
                End Date
              </Label>
              <DatePicker
                selected={endDate}
                onChange={handleEndDateChange}
                dateFormat="yyyy-MM-dd"
                placeholderText="Select end date"
                className="form-control"
                minDate={startDate} // Prevent selecting end date before start date
                maxDate={new Date()} // Prevent selecting future dates
                showYearDropdown
                showMonthDropdown
                dropdownMode="select"
              />
            </FormGroup>
          </Col>
          <Col md={4}>
            <FormGroup>
              <Label className="fw-bold">
                <FaSearch className="me-1" size={12} />
                Search
              </Label>
              <div className="position-relative">
                <Input
                  type="text"
                  placeholder="Search by plate number, status, or gate..."
                  value={filterText}
                  onChange={handleSearchChange}
                  className="pe-5"
                />
                {filterText && (
                  <Button
                    color="link"
                    size="sm"
                    className="position-absolute top-50 end-0 translate-middle-y pe-2"
                    onClick={() => {
                      setFilterText("")
                      setCurrentPage(1)
                      setResetPaginationToggle(!resetPaginationToggle)
                    }}
                    style={{ border: "none", background: "none" }}
                  >
                    <FaTimes size={12} />
                  </Button>
                )}
              </div>
            </FormGroup>
          </Col>
          <Col md={2} className="d-flex align-items-end">
            <FormGroup className="w-100">
              <Button
                color="secondary"
                size="sm"
                onClick={handleClearFilter}
                className="w-100 d-flex align-items-center justify-content-center"
              >
                <FaTimes className="me-1" size={12} />
                Clear All
              </Button>
            </FormGroup>
          </Col>
        </Row>

        {/* Results Summary */}
        <div className="mb-3">
          <div className="d-flex justify-content-between align-items-center">
            <div className="text-muted">
              <small>
                Showing {filteredData.length} of {totalRows} vehicle records
                {filterText && (
                  <span className="ms-2">
                    • Filtered by: "<strong>{filterText}</strong>"
                  </span>
                )}
              </small>
            </div>
            <div className="text-muted">
              <small>
                Date Range: {format(startDate, "MMM dd, yyyy")} - {format(endDate, "MMM dd, yyyy")} ({dateRangeInfo})
              </small>
            </div>
          </div>
        </div>

        {/* Data Table */}
        <DataTable
          columns={columns}
          data={filteredData}
          progressPending={loading}
          pagination
          paginationServer
          paginationTotalRows={totalRows}
          paginationPerPage={perPage}
          paginationRowsPerPageOptions={[10, 20, 50, 100]}
          onChangeRowsPerPage={handlePerRowsChange}
          onChangePage={handlePageChange}
          paginationResetDefaultPage={resetPaginationToggle}
          highlightOnHover
          pointerOnHover
          customStyles={customStyles}
          noDataComponent={
            <div className="py-8 text-center text-muted">
              <div className="mb-3">
                <FaCar size={48} className="opacity-50" />
              </div>
              <h6>No vehicle attendance records found</h6>
              <p className="mb-0">
                {filterText
                  ? `No vehicle records match your search criteria "${filterText}"`
                  : `No vehicle attendance records found for the selected date range`}
              </p>
              {(filterText || startDate !== defaultStartDate || endDate !== defaultEndDate) && (
                <Button color="primary" size="sm" onClick={handleClearFilter} className="mt-3">
                  Clear Filters
                </Button>
              )}
            </div>
          }
        />
      </CardBody>
    </Card>
  )
}

export default VehicleAttendance
